import type { AiInstructionsEditRequest } from './editorjs-ai-instructions-tune';
import type { GalleryEditRequest } from './editorjs-gallery-tool';

import { useCallback, useState } from 'react';

import { useDocumentChatComposer } from './document-chat-composer-context';
import { useDocumentContext } from './document-context';
import { useEditDocument } from './edit-document-context';
import { EditDocumentEditor } from './editor';
import { AiInstructionsModal } from './modals/ai-instructions-modal';
import { GalleryEditModal } from './modals/gallery-edit-modal';
import { GalleryImageModalProvider } from './modals/gallery-image-modal-context';

interface ViewDocumentViewerProps {
  apiHost: string;
  userEmail: string;
  onCreateSubpage: () => void;
  /** Navigate to a document path (client-side). Used by Subpages block links. */
  onNavigateToDocument?: (path: string) => void;
}

export function ViewDocumentViewer({
  apiHost,
  userEmail,
  onCreateSubpage,
  onNavigateToDocument,
}: ViewDocumentViewerProps) {
  const {
    documentId,
    isNewDocument,
    editorPlaceholderMuted,
    path,
    displayPath,
    editorInitialData,
    childrenItems,
  } = useDocumentContext();
  const { editorRef } = useEditDocument();

  const { appendEmphasis, focusComposer } = useDocumentChatComposer();
  const [galleryEditRequest, setGalleryEditRequest] =
    useState<GalleryEditRequest | null>(null);
  const [aiInstructionsRequest, setAiInstructionsRequest] =
    useState<AiInstructionsEditRequest | null>(null);

  const handleRequestGalleryEdit = useCallback(
    (request: GalleryEditRequest) => {
      setGalleryEditRequest(request);
    },
    [],
  );

  const handleRequestAiInstructionsEdit = useCallback(
    (request: AiInstructionsEditRequest) => {
      setAiInstructionsRequest(request);
    },
    [],
  );

  const handleAddToChat = useCallback(
    (blockId: string) => {
      appendEmphasis(blockId);
      focusComposer();
    },
    [appendEmphasis, focusComposer],
  );

  const handleUpload = useCallback(
    async (file: File): Promise<string> => {
      const { uploadImageFile } = await import('./upload-image');
      const result = await uploadImageFile(file, {
        apiHost,
        userEmail,
        ...(documentId ? { documentId } : {}),
      });
      return result.url;
    },
    [apiHost, documentId, userEmail],
  );

  return (
    <GalleryImageModalProvider>
      <GalleryEditModal
        request={galleryEditRequest}
        onDismiss={() => setGalleryEditRequest(null)}
        onUpload={handleUpload}
      />
      <AiInstructionsModal
        request={aiInstructionsRequest}
        onDismiss={() => setAiInstructionsRequest(null)}
      />
      <EditDocumentEditor
        key={documentId ?? path}
        ref={editorRef}
        value={editorInitialData}
        documentId={documentId}
        apiHost={apiHost}
        userEmail={userEmail}
        isNewDocument={isNewDocument}
        editorPlaceholderMuted={editorPlaceholderMuted}
        onUpload={handleUpload}
        parentPath={displayPath}
        children={childrenItems}
        onCreateSubpage={onCreateSubpage}
        onNavigateToDocument={onNavigateToDocument}
        onAddToChat={handleAddToChat}
        onRequestGalleryEdit={handleRequestGalleryEdit}
        onRequestAiInstructionsEdit={handleRequestAiInstructionsEdit}
      />
    </GalleryImageModalProvider>
  );
}
